import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import Table from 'components/Table';

import { IOperation, ISerie } from 'utils/interfaces';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { formatDate } from 'utils/functions';
import {
  ADMIN_OPERATIONS,
  CARDS,
  OPERATIONS_AUX,
  OPERATION_STATUS,
  PAY_MODES,
  PROOF_KINDS,
  REQUIRED_RULE,
  TO_ACCOUNTS,
} from 'utils/constants';
import SolidButton from 'components/SolidButton';
import { BUTTON_COLORS } from 'utils/enums';
import Modal from 'components/Modal';
import { useForm } from 'react-hook-form';
import BaseInput from 'components/Inputs/BaseInput';

export default function SignedPage() {
  const {
    register,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      doc_type: 'B',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [operations, setOperations] = useState<Array<IOperation>>([]);
  const [series, setSeries] = useState<Array<ISerie>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<IOperation | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState(false);

  const docType = watch('doc_type');

  const finalSeries = useMemo(
    () => series.filter((serie) => serie.doc_type === docType),
    [series, docType]
  );

  const submitAction = async (data: any) => {
    setActionLoading(true);
    let result: any = await generalApi.post('/emit_voucher', {
      operationId: actionData?._id,
      serieId: data.serie,
    });
    setActionLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    submitVoucher(result.data.voucherId);
    showSuccessToast('Comprobante emitido');
    setIsActionOpen(false);
    setActionData(undefined);
    setRefresh(true);
  };

  const submitVoucher = async (voucherId: string) => {
    let result: any = await generalApi.post('/submit_voucher', {
      voucherId,
    });
    if (!result.success) return showErrorToast(result.data.err);
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        control={control}
        watch={watch}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  useEffect(() => {
    const getOperations = async () => {
      setLoading(true);
      const result = await generalApi.get(`/signed_operations?page=${currentPage}&limit=10`);
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperations(result.data.docs);
      setTotalDocs(result.data.totalDocs);
      setLoading(false);
    };
    const getSeries = async () => {
      const result = await generalApi.get('/series');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setSeries(result.data);
    };
    if (refresh) {
      getSeries();
      getOperations();
      setRefresh(false);
    }
  }, [generalApi, refresh, currentPage]);

  useEffect(() => {
    setValue('serie', finalSeries[0]?._id);
  }, [finalSeries, setValue]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <form onSubmit={handleSubmit(submitAction)}>
            <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
              <div className="mt-3 text-center sm:mt-0 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                  Emitir comprobante
                </h3>
              </div>
            </div>
            <div onSubmit={handleSubmit(submitAction)} className="space-y-4 px-4 py-5 sm:p-6">
              {buildBaseInput('Tipo de comprobante', 'select', 'doc_type', REQUIRED_RULE, {
                options: [
                  { value: 'B', name: 'Boleta' },
                  { value: 'F', name: 'Factura' },
                  { value: 'T', name: 'Ticket' },
                ],
              })}
              {buildBaseInput('Serie', 'select', 'serie', REQUIRED_RULE, {
                options: finalSeries.map((obj) => ({ value: obj._id, name: obj.serie })),
              })}
            </div>
            <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <SolidButton
                className="w-auto sm:ml-3"
                isLoading={actionLoading}
                onClick={() => handleSubmit(submitAction)}
              >
                Emitir
              </SolidButton>
              <button
                type="button"
                onClick={() => {
                  setIsActionOpen(false);
                  setActionData(undefined);
                }}
                className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Operaciones firmadas</p>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando operaciones"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            currentPage={currentPage}
            totalDocs={totalDocs}
            onPageChange={(page) => {
              setCurrentPage(page);
              setRefresh(true);
            }}
            columns={[
              {
                title: 'ESTADO',
                dataKey: 'status',
                sortValue: (item: IOperation) => OPERATION_STATUS[item.status],
                render: (item: IOperation) => OPERATION_STATUS[item.status],
              },
              {
                title: 'CÓDIGO',
                dataKey: 'code',
              },
              {
                title: 'FECHA',
                dataKey: 'created_at',
                render: (item: IOperation) =>
                  formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false),
              },
              {
                title: 'Clase Ben',
                dataKey: 'to_account',
                render: (item: IOperation) => TO_ACCOUNTS[item.to_account],
              },
              {
                title: 'Cliente',
                key: 'customer',
                dataKey: 'user',
                sortValue: (item: IOperation) =>
                  item.user.level !== '30'
                    ? item.user.company_name
                    : `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`,
                render: (item: IOperation) =>
                  item.user.level !== '30'
                    ? item.user.company_name
                    : `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`,
              },
              {
                title: 'Usuario',
                dataKey: 'user',
                sortValue: (item: IOperation) =>
                  item.user.level !== '30'
                    ? `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
                    : '-',
                render: (item: IOperation) =>
                  item.user.level !== '30'
                    ? `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
                    : '-',
              },
              {
                title: 'Beneficiario',
                dataKey: 'to_name',
                sortValue: (item: IOperation) =>
                  item.user.level !== '30' && item.to_account === 'P'
                    ? item.user.company_name
                    : item.to_name,
                render: (item: IOperation) => (
                  <>
                    <p>
                      {item.user.level !== '30' && item.to_account === 'P' ? 'RUC' : item.to_doc}:{' '}
                      {item.user.level !== '30' && item.to_account === 'P'
                        ? item.user.username.split('|')[1]
                        : item.to_number}
                    </p>
                    <p>
                      {item.user.level !== '30' && item.to_account === 'P'
                        ? item.user.company_name
                        : item.to_name}
                    </p>
                  </>
                ),
              },
              {
                title: 'Importe',
                dataKey: 'amount',
              },
              {
                title: 'TIPO DE OPERACIÓN',
                dataKey: 'action',
                render: (item: IOperation) => ADMIN_OPERATIONS[OPERATIONS_AUX[item.action]],
              },
              {
                title: 'Precio',
                dataKey: 'final_rate',
              },
              {
                title: 'Ingresa',
                dataKey: 'origin_bank',
                sortValue: (item: IOperation) =>
                  item.action === 'C' ? item.amount * item.final_rate : item.amount,
                render: (item: IOperation) =>
                  item.amount && item.final_rate ? (
                    <>
                      <p>
                        {item.action === 'C'
                          ? (item.amount * item.final_rate)
                              ?.toFixed(2)
                              ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </p>
                      <p>{item.origin_bank}</p>
                    </>
                  ) : (
                    item.origin_bank
                  ),
              },
              {
                title: 'Sale',
                dataKey: 'destiny_bank',
                sortValue: (item: IOperation) =>
                  item.action === 'C' ? item.amount : item.amount * item.final_rate,
                render: (item: IOperation) =>
                  item.amount && item.final_rate ? (
                    <>
                      <p>
                        {item.action === 'C'
                          ? item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : (item.amount * item.final_rate)
                              ?.toFixed(2)
                              ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </p>
                      <p>{item.destiny_bank}</p>
                    </>
                  ) : (
                    item.destiny_bank
                  ),
              },
              {
                title: 'Método de pago',
                dataKey: 'pay_mode',
                render: (item: IOperation) => (
                  <>
                    <p>{PAY_MODES[item.pay_mode]}</p>
                    {item.pay_mode === 'RC' ? (
                      <>
                        <p>{item.to_acc_number}</p>
                        {item.to_cci && <p>CCI: {item.to_cci}</p>}
                      </>
                    ) : item.to_card ? (
                      <>
                        <p>{CARDS[item.to_card]}</p>
                        <p>{item.to_acc_number}</p>
                      </>
                    ) : (
                      '-'
                    )}
                  </>
                ),
              },
              {
                title: 'Prueba',
                dataKey: 'proof',
                render: (item: IOperation) => (
                  <>
                    {item.proof === 'mail' ? (
                      <p>{PROOF_KINDS[item.proof]}</p>
                    ) : item.proof === 'file' ? (
                      <a
                        href={item.proof_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700 underline hover:text-blue-800"
                      >
                        {PROOF_KINDS[item.proof]}
                      </a>
                    ) : (
                      '-'
                    )}
                  </>
                ),
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: IOperation) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      color={BUTTON_COLORS.BLUE}
                      onClick={() => {
                        setIsActionOpen(true);
                        setActionData(item);
                      }}
                    >
                      Emitir
                    </SolidButton>
                  </div>
                ),
              },
            ]}
            data={operations}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
